import React, { useContext } from 'react'

import { Field, FormInstance } from 'rc-field-form'
import { Meta, Rule } from 'rc-field-form/es/interface.d'
import styled, { css, useTheme } from 'styled-components'

import { SmallLoader } from '@atoms/index'
import { DisabledEffect, ResponsiveProperty, ResponsivePXValue, theme } from '@components/Theme'

import { FormContext, FormValue, InputProps } from './Form'
import { InputWrapper } from './InputWrapper'

const LightSelected = css`
  border-color: ${theme.colors.greys.mineshaft};
  border-width: 2px;
  ${ResponsivePXValue('padding', '15px')}
`

const DarkSelected = css`
  border-color: ${theme.colors.greys.mineshaft};
  border-width: 2px;
  opacity: 0.95;
  ${ResponsivePXValue('padding', '15px')}
`

const Container = styled.div<{ $direction: 'column' | 'row' }>`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  flex-direction: ${props => props.$direction};
  ${ResponsiveProperty('align-items', { mobile: 'center', tablet: 'flex-start', desktop: 'flex-start' })}
  ${ResponsiveProperty('justify-content', { mobile: 'space-between' })}
`

const ButtonOuterContainer = styled.div<{ $selected: boolean, $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-style: solid;
  border-radius: 50%;
  width: 18px;
  min-width: 18px;
  height: 18px;
  min-height: 18px;
  border-width: 1px;
  border-color: ${props => props.theme.colors.whites.silver};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  ${ResponsivePXValue('margin', { mobile: '1px', tablet: '2px', desktop: '1px' })}
  ${props => props.$selected && css`
    border-color: ${props.theme.colors.greys.mineshaft};
    box-shadow: inset 0 0 0 6px ${props.theme.colors.greys.mineshaft};
  `}
  ${props => props.$disabled && css`
    border-color: ${props.theme.colors.whites.silver};
    ${DisabledEffect};
    box-shadow: none;
  `}
`

const ItemLabel = styled.div<{ $color: string, $isMiniCard: boolean }>`
  font-family: gordita, sans-serif;
  font-weight: 500;
  position: relative;
  color: ${props => props.$color};
  transition: color 0.2s ease;
  ${ResponsivePXValue('top', '1px')}
  ${ResponsivePXValue('font-size', '14px')}
  ${props => props.$isMiniCard
    ? ResponsivePXValue('margin-left', '4px')
    : css`
        ${ResponsivePXValue('margin-left', '8px')}
        ${ResponsivePXValue('margin-right', '8px')}
      `
}
`

const ItemContainer = styled.div<{ $componentTheme: 'light' | 'dark' | 'orange', $outline: boolean, $selected: boolean, $loading: boolean, $disabled: boolean, $isMiniCard: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 1;
  border-radius: 4px;
  align-self: stretch;
  position: relative;
  transition: background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease, padding 0.2s ease;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  cursor: ${props => props.$loading || props.$disabled ? 'not-allowed' : 'pointer'};
  border-width: 1px;
  border-style: solid;
  border-color: ${props => {
    if (!props.$outline) return props.theme.colors.misc.transparent
    switch (props.$componentTheme) {
      case 'light': return props.theme.colors.whites.pureWhite
      case 'orange':
      case 'dark':
      default: return props.theme.colors.whites.silver
    }
  }};
  background-color: ${props => {
    switch (props.$componentTheme) {
      case 'light':
      case 'orange': return props.theme.colors.whites.pureWhite
      case 'dark':
      default: return props.theme.colors.misc.transparent
    }
  }};
  ${props => props.$outline ? ResponsivePXValue('padding', '16px') : null};
  ${props => props.$outline ? ResponsivePXValue('margin', '0 16px 16px 16px') : ResponsivePXValue('margin', '0 12px 0 0')};
  ${ResponsiveProperty('flex-direction', { mobile: 'row', tablet: 'row', desktop: 'row' })}
  ${ResponsiveProperty('justify-content', { mobile: 'flex-start', tablet: 'center', desktop: 'center' })}
  ${props => props.$outline && props.$selected && (
    props.$componentTheme === 'light'
      ? LightSelected
      : props.$componentTheme === 'dark'
        ? DarkSelected
        : props.$componentTheme === 'orange'
          ? css`
      background-color: ${props.theme.colors.oranges.coral};
      border-color: ${props.theme.colors.oranges.coral};
      border-width: 1px;
      ${ItemLabel} * {
        color: ${props.theme.colors.whites.pureWhite} !important;
      }
      ${ButtonOuterContainer} {
        border-color: ${props.theme.colors.whites.pureWhite};
        box-shadow: inset 0 0 0 4px ${props.theme.colors.whites.pureWhite};
      }
    `
          : DarkSelected
  )}
  ${props => props.$isMiniCard && css`
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    background-color: transparent !important;
    opacity: ${props.$disabled ? 0.5 : 1} !important;
  `}
`

const ItemCountBubble = styled.div<{ $count: number }>`
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.greens.fruitSalad};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  top: 0;
  right: 0;
  transform: translate(40%, -40%);
  ${ResponsivePXValue('height', { mobile: '16px', tablet: '16px', desktop: '18px' })}
  ${ResponsivePXValue('width', { mobile: '16px', tablet: '16px', desktop: '18px' })}
  color: ${props => props.theme.colors.whites.pureWhite};
  .counter {
    font-size: 10px;
    line-height: 1;
    font-weight: 500;
    transform: translateY(0.5px);
  }
`

const LoadingContainer = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 25px;
  height: 25px;
  z-index: 2;
`

interface Values {
  value?: FormValue
  onChange?: (value: FormValue) => void
}

export interface RadioButtonOption {
  element: React.ReactNode
  value: string | number
  disabled?: boolean
  className?: string
  count?: number
}

export interface RadioButtonProps extends InputProps {
  rules?: Rule[]
  variant?: 'vertical' | 'horizontal'
  outline?: boolean
  componentTheme?: 'light' | 'dark' | 'orange'
  options?: RadioButtonOption[]
  className?: string
  isMiniCard?: boolean
  beforeChange?: (form: FormInstance) => boolean
  onPreventChange?: () => void
}

interface RadioButtonInnerProps extends RadioButtonProps {
  control: Values
  meta: Meta
  form: FormInstance
}

const RadioInner = React.memo((props: RadioButtonInnerProps): JSX.Element => {
  const {
    rules,
    label,
    showLabel = true,
    variant = 'horizontal',
    componentTheme = 'dark',
    outline = true,
    options,
    loading,
    disabled,
    control,
    meta,
    form,
    className,
    isMiniCard = false,
    showOptional = false,
    beforeChange,
    onPreventChange,
  } = props

  const currentTheme = useTheme()
  const { loading: formLoading, disabled: formDisabled } = useContext(FormContext)
  const availableOptions: RadioButtonOption[] = options ?? []
  const required = rules?.some(rule => typeof rule === 'object' && rule.required)
  const error = meta.errors?.[0]
  const { value, onChange } = control

  const isEffectivelyDisabled = disabled || formDisabled
  const isEffectivelyLoading = loading || formLoading

  const _handleChange = (option: RadioButtonOption) => {
    if (isEffectivelyDisabled || isEffectivelyLoading || option?.disabled) {
      return
    }
    if (beforeChange && !beforeChange(form)) {
      onPreventChange?.()
      return
    }
    const newValue = option.value
    onChange?.(newValue)
  }

  const direction = variant === 'horizontal' ? 'row' : 'column'

  return (
    <InputWrapper
      required={required}
      label={label}
      showLabel={showLabel}
      error={error}
      className={className}
      isMiniCard={isMiniCard}
      showOptional={showOptional}>
      <Container $direction={direction} className='radio-group-container'>
        {availableOptions.map((option) => {
          const isSelected = option.value === value
          const isOptionDisabled = isEffectivelyDisabled || option?.disabled
          const currentLabelColor = isOptionDisabled || isEffectivelyLoading
            ? currentTheme.colors.whites.silver
            : currentTheme.colors.greys.tundora

          return (
            <ItemContainer
              className={`radio-item-container ${option?.className ?? ''}`}
              $loading={isEffectivelyLoading}
              key={option.value.toString()}
              $componentTheme={componentTheme}
              $outline={outline}
              $selected={isSelected}
              $disabled={isOptionDisabled}
              $isMiniCard={isMiniCard}
              onClick={() => _handleChange(option)}
              role="radio"
              aria-checked={isSelected}
              aria-disabled={isOptionDisabled}
              tabIndex={isOptionDisabled ? -1 : 0}
            >
              <ButtonOuterContainer
                $disabled={isOptionDisabled || isEffectivelyLoading}
                $selected={isSelected}
                aria-hidden="true"
              />
              {option.element && (
                <ItemLabel
                  className='item-label'
                  $color={currentLabelColor}
                  $isMiniCard={isMiniCard}>
                  {option.element}
                </ItemLabel>
              )}
              {option.count != null && option.count > 0 && !isMiniCard && outline && (
                <ItemCountBubble aria-label={`${option.count} available`} $count={option.count}>
                  <span className='counter'>{option.count}</span>
                </ItemCountBubble>
              )}
            </ItemContainer>
          )
        })}
        {isEffectivelyLoading && (
          <LoadingContainer aria-label="Loading options">
            <SmallLoader color={currentTheme.colors.whites.silver} />
          </LoadingContainer>
        )}
      </Container>
    </InputWrapper>
  )
})

RadioInner.displayName = 'RadioInner'

export function RadioButton(props: RadioButtonProps): JSX.Element {
  const { name, rules, ...restProps } = props

  if (!name) {
    console.warn('RadioButton component requires a name prop to be used within a Form.')
    return <></>
  }

  return (
    <Field name={name} rules={rules}>
      {(control: Values, meta: Meta, form: FormInstance) => (
        <RadioInner {...restProps} control={control} meta={meta} form={form} />
      )}
    </Field>
  )
}
RadioButton.displayName = 'RadioButton'
